import { Button, Typography, Box } from '@mui/material';
import type { FallbackRender } from '@sentry/react';

/**
 * Fallback de nivel raíz de la aplicación.
 * Se usa en el Sentry.ErrorBoundary de main.tsx.
 * Cubre crashes totales que hacen imposible renderizar cualquier otra cosa.
 */
const AppCrashFallback: FallbackRender = ({ error, resetError, eventId }) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={3}
      px={3}
      sx={{ backgroundColor: '#f8fafc' }}
    >
      <Box
        component="img"
        src="/assets/img/error-icon.svg"
        alt="Error"
        width={80}
        height={80}
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />

      <Typography variant="h5" fontWeight={700} color="text.primary" textAlign="center">
        Algo salió mal
      </Typography>

      <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={420}>
        No fue posible completar la operación. Por favor, intentá nuevamente o recargá la página.
        {eventId && (
          <Box component="span" display="block" mt={1} sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#94a3b8' }}>
            Referencia: {eventId}
          </Box>
        )}
      </Typography>

      <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
        <Button
          variant="contained"
          onClick={resetError}
          sx={{ backgroundColor: '#0050da', '&:hover': { backgroundColor: '#0040b0' } }}
        >
          Intentar nuevamente
        </Button>
        <Button
          variant="outlined"
          onClick={() => window.location.assign('/')}
          color="inherit"
        >
          Recargar la página
        </Button>
      </Box>

      {import.meta.env.DEV && error instanceof Error && (
        <Box
          component="pre"
          sx={{
            mt: 2,
            p: 2,
            maxWidth: 700,
            width: '100%',
            overflow: 'auto',
            backgroundColor: '#1e293b',
            color: '#f1f5f9',
            borderRadius: 1,
            fontSize: '0.75rem',
          }}
        >
          {error.message}
          {'\n\n'}
          {error.stack}
        </Box>
      )}
    </Box>
  );
};

export default AppCrashFallback;
